import React from 'react';
import { Facebook, Linkedin, Mail, MessageCircle, Phone } from 'lucide-react';
import { Button } from './ui/button';

const WHATSAPP_NUMBER_DISPLAY = '+1 345 321 5557';
const WHATSAPP_NUMBER_DIGITS = '13453215557';
const SUPPORT_EMAIL = 'info@smartconnections.club';
const FACEBOOK_URL = 'https://www.facebook.com/profile.php?id=61589708038329';
const LINKEDIN_URL = 'https://www.linkedin.com/company/smart-connections-t-a-tbc/?viewAsMember=true';

export const Footer = () => {
  return (
    <footer className="bg-navy text-white">
      {/* Contact CTA Section */}
      <div className="py-16 px-4 max-w-7xl mx-auto">
        <div className="bg-coral rounded-[2rem] p-10 sm:p-12 text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Do you have any questions?
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            We will provide detailed information about our products, pricing, and bulk orders.
            Get in touch with us directly — we usually reply within a few hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              data-testid="footer-whatsapp-btn"
              onClick={() =>
                window.open(`https://wa.me/${WHATSAPP_NUMBER_DIGITS}`, '_blank', 'noopener,noreferrer')
              }
              className="bg-white hover:bg-white/90 text-navy font-bold px-6 py-3 rounded-full inline-flex items-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Chat on WhatsApp
            </Button>
            <Button
              data-testid="footer-email-btn"
              onClick={() => (window.location.href = `mailto:${SUPPORT_EMAIL}`)}
              className="bg-yellow-400 hover:bg-yellow-500 text-navy font-bold px-6 py-3 rounded-full inline-flex items-center gap-2"
            >
              <Mail className="w-5 h-5" />
              Email Us
            </Button>
          </div>
        </div>

        {/* Footer Links */}
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          {/* Products */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide mb-4">PRODUCTS</h3>
            <ul className="space-y-3">
              <li><a href="/digital-cards" className="text-gray-400 hover:text-white transition-colors">Digital Business Cards</a></li>
              <li><a href="/review-cards" className="text-gray-400 hover:text-white transition-colors">Review Cards</a></li>
              <li><a href="/shop" className="text-gray-400 hover:text-white transition-colors">Shop All Cards</a></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide mb-4">COMPANY</h3>
            <ul className="space-y-3">
              <li><a href="/about" className="text-gray-400 hover:text-white transition-colors">About Us</a></li>
              <li><a href="/enterprise" className="text-gray-400 hover:text-white transition-colors">Enterprise & Team</a></li>
              <li><a href="/artist" className="text-gray-400 hover:text-white transition-colors">Artist</a></li>
              <li><a href="/contact" className="text-gray-400 hover:text-white transition-colors">Contact Us</a></li>
              <li><a href="/faq" className="text-gray-400 hover:text-white transition-colors">FAQ</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <img
              src="https://customer-assets.emergentagent.com/job_review-cards-1/artifacts/ip2pgeii_JPEG-02-removebg-preview.png"
              alt="Smart Connections"
              className="h-20 w-auto mb-6"
            />
            <a
              href={`tel:+${WHATSAPP_NUMBER_DIGITS}`}
              className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 mb-2"
              data-testid="footer-phone-link"
            >
              <Phone className="w-4 h-4" />
              {WHATSAPP_NUMBER_DISPLAY}
            </a>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 mb-6"
              data-testid="footer-email-link"
            >
              <Mail className="w-4 h-4" />
              {SUPPORT_EMAIL}
            </a>
            <div className="flex gap-3">
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="footer-facebook-link"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-testid="footer-linkedin-link"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER_DIGITS}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                data-testid="footer-whatsapp-link"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* CTA */}
          <div>
            <Button
              onClick={() => (window.location.href = '/contact')}
              className="bg-yellow-400 hover:bg-yellow-500 text-navy font-bold px-6 py-3 rounded-full w-full mb-4"
            >
              GET IN TOUCH
            </Button>
            <Button
              onClick={() => (window.location.href = '/shop')}
              className="bg-white/10 hover:bg-white/20 text-white font-bold px-6 py-3 rounded-full w-full"
            >
              SHOP NOW
            </Button>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Smart Connections — All Rights Reserved
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <a href="/about" className="text-gray-400 hover:text-white text-sm transition-colors">About Us</a>
              <a href="/contact" className="text-gray-400 hover:text-white text-sm transition-colors">Contact</a>
              <a href="/faq" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="footer-faq-link">FAQ</a>
              <a href="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="footer-privacy-link">Privacy Policy</a>
              <a href="/terms" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="footer-terms-link">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
